import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertCircle, BarChart2, CheckCircle, Clock, Cpu, Database, FileSearch, Zap,
  Loader2, RefreshCw, AlertTriangle, Radio,
  Terminal, BarChart3, Layers, ListTodo, ShieldCheck,
  Map, CalendarClock, History, Grid
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useScraperLogs from '../../hooks/useScraperLogs';
import LiveLogs from './LiveLogs';
import LogSummary from './LogSummary';
import ScraperMetricsPanel from './ScraperMetricsPanel';
import ActiveScrapePanel from './ActiveScrapePanel';
import ScrapeHistoryCard from './ScrapeHistoryCard';
import ScheduledScrapesPanel from './ScheduledScrapesPanel';
import AdvancedSection from './AdvancedSection';
import SupplierDetailModal from './SupplierDetailModal';
import CoverageDashboard from './CoverageDashboard';
import JobQueue from './JobQueue';
import DataIntegrity from './DataIntegrity';
import DetailExtractionPanel from './DetailExtractionPanel';
import POComparisonView from './POComparisonView';
import ScrapeTriggerForm from './ScrapeTriggerForm';
import AgencyCoverageMatrix from './AgencyCoverageMatrix';
import AlertsStrip from './AlertsStrip';
import WorkerStatusPanel from './WorkerStatusPanel';
import { getDeptCode } from './constants';
import './ScraperDashboard.css';
import AutomationPanel from '../AutomationPanel/AutomationPanel';
import CompletenessCard from './CompletenessCard';
import DetailCoverageCard from './DetailCoverageCard';
import ClassificationStatsCard from './ClassificationStatsCard';
import SchedulerToggle from './SchedulerToggle';
import SchedulerHealthCard from './SchedulerHealthCard';
import ProbeStatusCard from './ProbeStatusCard';
import BcpIngestionCard from './BcpIngestionCard';
import DailyRefreshCard from './DailyRefreshCard';
import DetailJobsToggle from './DetailJobsToggle';

// =====================================================
// STATUS BADGE
// =====================================================
function WorkerBadge({ jobHealth, jobStats }) {
  const [showDetail, setShowDetail] = useState(false);

  if (!jobHealth && !jobStats) return <span className="sd-badge badge-muted"><Loader2 size={12} className="sd-spin" /></span>;

  const completed1h = jobHealth?.throughput?.completed_1h || 0;
  const failed1h = jobHealth?.throughput?.failed_1h || 0;
  const avgDuration = jobHealth?.throughput?.avg_duration_s;
  const staleCount = jobHealth?.stale_jobs?.length || 0;

  // Use jobStats for accurate running count (from /api/scraper/jobs/stats)
  // Fall back to jobHealth.statuses if stats not available
  const runningCount = jobStats?.running ||
    (jobHealth?.statuses || []).find(s => s.status === 'running')?.count || 0;
  const pendingCount = jobStats?.pending || 0;

  // Determine worker status from real-time job counts
  let status, label, Icon, cls;
  if (runningCount > 0) {
    status = 'active';
    label = `${runningCount} Running`;
    Icon = Loader2;
    cls = 'badge-success';
  } else if (pendingCount > 0) {
    status = 'waiting';
    label = `${pendingCount} Queued`;
    Icon = Clock;
    cls = 'badge-warning';
  } else if (completed1h > 0 || failed1h > 0) {
    status = 'idle';
    label = 'Worker Idle';
    Icon = CheckCircle;
    cls = 'badge-success';
  } else if (staleCount > 0) {
    status = 'stale';
    label = 'Stale Job';
    Icon = AlertTriangle;
    cls = 'badge-warning';
  } else {
    // No activity in the last hour — check if any jobs completed in last 24h
    const anyRecent = (jobHealth?.statuses || []).some(s => s.count > 0);
    if (anyRecent) {
      status = 'idle';
      label = 'Worker Idle';
      Icon = Clock;
      cls = 'badge-muted';
    } else {
      status = 'offline';
      label = 'No Activity';
      Icon = AlertCircle;
      cls = 'badge-danger';
    }
  }

  return (
    <span className="sd-worker-badge-wrap" style={{ position: 'relative' }}>
      <span
        className={`sd-badge ${cls} sd-badge-clickable`}
        onClick={() => setShowDetail(!showDetail)}
        title="Click for worker details"
      >
        <Icon size={12} className={status === 'active' ? 'sd-spin' : ''} />{label}
      </span>
      {showDetail && (
        <div className="sd-worker-detail">
          {runningCount > 0 && (
            <div className="sd-worker-detail-row">
              <span>Running:</span>
              <span>{runningCount} jobs</span>
            </div>
          )}
          {pendingCount > 0 && (
            <div className="sd-worker-detail-row">
              <span>Queued:</span>
              <span>{pendingCount} jobs</span>
            </div>
          )}
          <div className="sd-worker-detail-row">
            <span>Last hour:</span>
            <span>{completed1h} completed, {failed1h} failed</span>
          </div>
          {avgDuration && (
            <div className="sd-worker-detail-row">
              <span>Avg duration:</span>
              <span>{Math.round(avgDuration)}s</span>
            </div>
          )}
          {staleCount > 0 && (
            <div className="sd-worker-detail-row sd-worker-detail-warn">
              <span>{staleCount} stale job{staleCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// =====================================================
// MAIN DASHBOARD — SCRAPER-FIRST LAYOUT
// =====================================================
function ScraperDashboard({ selectedDepartment }) {
  const deptCode = getDeptCode(selectedDepartment);
  const [loading,       setLoading]       = useState(true);
  const [triggerLoading,setTriggerLoading]= useState(false);
  const [lastRefresh,   setLastRefresh]   = useState(null);
  const [autoRefresh,   setAutoRefresh]   = useState(true);
  const [refreshKey,    setRefreshKey]    = useState(0);
  const [triggerResult, setTriggerResult] = useState(null);
  const [showLiveLogs,  setShowLiveLogs]  = useState(false);
  const [activeScrape,  setActiveScrape]  = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [comparisonDocNum, setComparisonDocNum] = useState(null);
  const [collapseSignal, setCollapseSignal] = useState(0); // bump to remount AdvancedSections after bulk change
  const intervalRef = useRef(null);

  // Bulk expand/collapse every AdvancedSection with a storageKey. Writes
  // all keys under 'scraper-dashboard:collapsed:' then bumps a signal so
  // mounted AdvancedSection instances re-read their initial state.
  const setAllCardsExpanded = useCallback((expanded) => {
    try {
      const prefix = 'scraper-dashboard:collapsed:';
      // Collect existing keys so we don't invent new ones, but also include
      // any storageKey this page uses even if not yet in localStorage.
      const known = [
        'run-scrape', 'alerts', 'scheduler-health', 'probe-status',
        'daily-refresh', 'worker-health', 'agency-coverage', 'job-queue',
        'detail-extraction', 'coverage-tracking', 'scheduled-scrapes',
        'detail-coverage', 'po-classification', 'scrape-completeness',
        'automation', 'data-integrity', 'performance-metrics', 'live-logs',
      ];
      for (const k of known) {
        window.localStorage.setItem(prefix + k, String(expanded));
      }
    } catch (_) { /* ignore */ }
    setCollapseSignal((n) => n + 1);
  }, []);

  // Worker status from job activity (not broken /healthz endpoint)
  const { data: jobHealth } = useQuery({
    queryKey: ['scraper', 'jobs', 'health'],
    queryFn: () => apiClient.get('/api/scraper/jobs/health'),
    refetchInterval: 10000,
  });

  // Real-time job stats for accurate running/pending counts
  const { data: jobStats } = useQuery({
    queryKey: ['scraper', 'jobs', 'stats'],
    queryFn: () => apiClient.get('/api/scraper/jobs/stats'),
    refetchInterval: 5000,
  });

  const isWorkerActive = (jobStats?.running || 0) > 0 ||
    !!(jobHealth?.statuses || []).find(s => s.status === 'running');

  // Real-time scraper logs via WebSocket
  const {
    logs: scraperLogs,
    connected: logsConnected,
    error: logsError,
    stats: logsStats,
    usingPolling,
    clear: clearLogs,
    getFilteredLogs
  } = useScraperLogs();

  // Scrape history state
  const [scrapeHistory, setScrapeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchScrapeHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await apiClient.get('/api/scraper/results/recent');
      setScrapeHistory(Array.isArray(data) ? data : []);
    } catch (e) { console.error('History fetch failed', e); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { fetchScrapeHistory(); }, [fetchScrapeHistory]);

  // Filter scrape history to selected department
  const filteredHistory = useMemo(() => {
    if (!deptCode) return scrapeHistory;
    return scrapeHistory.filter(run => run.criteria?.departmentCode === deptCode);
  }, [scrapeHistory, deptCode]);

  const fetchStatus = useCallback(async () => {
    try {
      await apiClient.get('/api/scraper/status');
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch scraper status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchStatus, 15000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchStatus]);

  const handleTrigger = async ({ type, dateFrom, dateTo, departmentCode, deptAcronym, acquisitionType, supplierId, _label }) => {
    setTriggerLoading(true);
    setTriggerResult(null);

    try {
      let result;
      if (type === 'survey') {
        // Survey mode — POST to scraper service directly (SSE response, fire-and-forget)
        try {
          const scraperBase = process.env.REACT_APP_SCRAPER_URL || 'https://techstack-scraper.onrender.com';
          await fetch(`${scraperBase}/scprs/survey`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ departmentCode, deptAcronym, dateFrom, dateTo, acquisitionType: acquisitionType || null }),
          });
          setTriggerResult({ ok: true, msg: `Survey started for ${deptAcronym || departmentCode} — expected count will appear in matrix when done (~20s)` });
        } catch(e) {
          setTriggerResult({ ok: false, msg: 'Survey failed: ' + e.message });
        }
        setTriggerLoading(false);
        return;
      }
      if (type === 'fiscal') {
        result = await apiClient.post('/api/scraper/scrape/fiscal', { supplierId, dateFrom, dateTo });
      } else if (type === 'canary') {
        result = await apiClient.post('/api/scraper/canary', {});
      } else {
        result = await apiClient.post('/api/scraper/scrape/scprs', { dateFrom, dateTo, departmentCode, acquisitionType });
      }

      const job = result?.job;
      if (job?.id) {
        // Show active scrape panel — it polls the job status directly
        setActiveScrape({ jobId: job.id });
        setTriggerResult(null);
      } else {
        setTriggerResult({ ok: true, message: 'Job queued', data: result });
      }
    } catch (err) {
      setTriggerResult({ ok: false, message: err.message || 'Failed to queue job' });
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleCancelScrape = () => {
    setActiveScrape(null);
    setTriggerResult(null);
  };

  return (
    <div className="sd-container">

      {/* HEADER */}
      <div className="sd-header">
        <div className="sd-header-left">
          <Radio size={20} className={isWorkerActive ? 'sd-pulse' : ''} />
          <h2>Scraper Dashboard</h2>
          <WorkerBadge jobHealth={jobHealth} jobStats={jobStats} />
          <SchedulerToggle />
          <DetailJobsToggle />
        </div>
        <div className="sd-header-right">
          <label className="sd-auto-refresh">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button
            className="sd-btn sd-btn-ghost"
            onClick={() => setAllCardsExpanded(true)}
            title="Expand every card on this page"
          >
            Expand all
          </button>
          <button
            className="sd-btn sd-btn-ghost"
            onClick={() => setAllCardsExpanded(false)}
            title="Collapse every card on this page"
          >
            Collapse all
          </button>
          <button className="sd-btn sd-btn-ghost" onClick={fetchStatus} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'sd-spin' : ''} /> Refresh
          </button>
          {lastRefresh && <span className="sd-last-refresh">Updated {lastRefresh.toLocaleTimeString()}</span>}
        </div>
      </div>

      {loading ? (
        <div className="sd-loading"><Loader2 size={32} className="sd-spin" /><p>Loading...</p></div>
      ) : (
        <React.Fragment key={collapseSignal}>
          {/* RUN SCRAPE */}
          <AdvancedSection
            title="Run Scrape"
            icon={Zap}
            description="Trigger a manual bulk export scrape"
            defaultExpanded={true}
            storageKey="run-scrape"
          >
            <div className="scrape-card__inner">
              <ScrapeTriggerForm onTrigger={handleTrigger} loading={triggerLoading} selectedDepartment={selectedDepartment} />
              {triggerResult && !activeScrape && (
                <div className={`scrape-card__result ${triggerResult.ok ? 'is-success' : 'is-error'}`}>
                  {triggerResult.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                  <span>{triggerResult.message}</span>
                </div>
              )}
            </div>
          </AdvancedSection>

          {/* ALERTS */}
          <AdvancedSection
            title="Alerts"
            icon={AlertCircle}
            description="Failed jobs, stale runs, and escalations needing attention"
            defaultExpanded={true}
            storageKey="alerts"
          >
            <AlertsStrip departmentCode={deptCode} />
          </AdvancedSection>

          {/* SCHEDULER HEALTH */}
          <AdvancedSection
            title="Scheduler Health"
            icon={Activity}
            description="Runtime liveness of the coverage cron, backlog, and last-tick time"
            defaultExpanded={true}
            storageKey="scheduler-health"
          >
            <SchedulerHealthCard />
          </AdvancedSection>

          {/* CALEPROCURE PROBE HEALTH — probe state, graduation, suspect jobs, recent alerts */}
          <AdvancedSection
            title="CaleProcure Probe"
            icon={ShieldCheck}
            description="Site accessibility probe, graduation test progress, recent state alerts"
            defaultExpanded={true}
            storageKey="probe-status"
          >
            <ProbeStatusCard />
          </AdvancedSection>

          {/* BCP INGESTION — the DOF budget-proposal pipeline. Separate
              harvester (techstack-scraper on the Mini, PM2 bcp-refresh-weekly)
              from the CaleProcure scraper above, so it needs its own liveness
              read: a current-looking corpus is not evidence it is running. */}
          <AdvancedSection
            title="BCP Ingestion"
            icon={FileSearch}
            description="Last BCP scrape, last new proposal found, DOF count vs ours, failed downloads, missing/changed documents"
            defaultExpanded={true}
            storageKey="bcp-ingestion"
          >
            <BcpIngestionCard />
          </AdvancedSection>

          {/* DAILY REFRESH — keep 10-year history current via daily + weekly sweeps */}
          <AdvancedSection
            title="Daily Refresh"
            icon={CalendarClock}
            description="Automated daily + weekly bulk exports to keep history current"
            defaultExpanded={false}
            storageKey="daily-refresh"
          >
            <DailyRefreshCard />
          </AdvancedSection>

          {/* WORKER STATUS */}
          <AdvancedSection
            title="Worker Health"
            icon={Activity}
            description="Monitor scraper worker status and send commands"
            defaultExpanded={false}
            storageKey="worker-health"
          >
            <WorkerStatusPanel />
          </AdvancedSection>

          {/* Active Scrape Progress */}
          {activeScrape && (
            <section className="sd-card">
              <ActiveScrapePanel
                jobId={activeScrape.jobId}
                onCancel={handleCancelScrape}
                onDone={async (job) => {
                  setActiveScrape(null);
                  if (job?.status === 'completed' || job?.status === 'partial') {
                    const r = typeof job.result === 'string' ? JSON.parse(job.result) : (job.result || {});
                    setTriggerResult({
                      ok: (r.suppliers || r.saved || 0) > 0,
                      message: `Scrape completed: ${r.suppliers || r.saved || 0} POs in ${r.duration || '?'}s`,
                      suppliers: r.suppliers || r.saved || 0,
                    });
                    fetchScrapeHistory();
                    // Refresh materialized view so timeline data updates with new POs
                    try {
                      await apiClient.post('/api/gap-analysis/refresh');
                    } catch (e) {
                      console.error('[ScraperDashboard] Failed to refresh gap analysis view:', e);
                    }
                    setRefreshKey(k => k + 1);
                  } else if (job?.status === 'failed') {
                    setTriggerResult({ ok: false, message: job.error || 'Scrape failed' });
                  }
                }}
              />
            </section>
          )}

          {/* AGENCY COVERAGE MATRIX */}
          <AdvancedSection title="Agency Coverage" icon={Grid} defaultExpanded={true} storageKey="agency-coverage" description="IT Goods & Services completeness by department">
            <AgencyCoverageMatrix departmentCode={deptCode} departmentId={selectedDepartment?.department_id} refreshKey={refreshKey} />
          </AdvancedSection>

          {/* JOB QUEUE (includes Runs tab for recent scrapes) */}
          <AdvancedSection title="Job Queue" icon={ListTodo} defaultExpanded={true} storageKey="job-queue" description="Pending, running, completed, and failed scraper jobs">
            <JobQueue
              autoRefreshInterval={5000}
              hideHeader
              departmentCode={deptCode}
              scrapeHistory={filteredHistory}
              historyLoading={historyLoading}
              onRefreshHistory={fetchScrapeHistory}
              onSupplierClick={setSelectedSupplier}
            />
          </AdvancedSection>

          {/* DETAIL EXTRACTION CONTROLS */}
          <AdvancedSection title="Detail Extraction Controls" icon={Layers} description="PO detail queue, throttle, and batch controls" defaultExpanded={false} storageKey="detail-extraction">
            <DetailExtractionPanel departmentCode={deptCode} onViewComparison={setComparisonDocNum} />
          </AdvancedSection>

          {/* COVERAGE TRACKING */}
          <AdvancedSection title="Coverage Tracking" icon={Map} description="Campaign-based coverage" defaultExpanded={false} storageKey="coverage-tracking">
            <CoverageDashboard departmentCode={deptCode} />
          </AdvancedSection>

          {/* SCHEDULED SCRAPES */}
          <AdvancedSection title="Scheduled Scrapes" icon={CalendarClock} description="Automated scrape schedules" defaultExpanded={false} storageKey="scheduled-scrapes">
            <ScheduledScrapesPanel hideHeader departmentCode={deptCode} />
          </AdvancedSection>

          {/* DETAIL COVERAGE */}
          <AdvancedSection title="Detail Coverage" icon={FileSearch} description="POs with vs without detail extraction data" defaultExpanded={false} storageKey="detail-coverage">
            <DetailCoverageCard />
          </AdvancedSection>

          {/* PO CLASSIFICATION */}
          <AdvancedSection title="PO Classification" icon={Cpu} description="Mainframe/midstack detection from bulk exports" defaultExpanded={false} storageKey="po-classification">
            <ClassificationStatsCard />
          </AdvancedSection>

          {/* SCRAPE COMPLETENESS */}
          <AdvancedSection title="Scrape Completeness" icon={BarChart2} description="How complete are our scrapes? POs saved vs expected" defaultExpanded={false} storageKey="scrape-completeness">
            <CompletenessCard />
          </AdvancedSection>

          {/* AUTOMATION */}
          <AdvancedSection title="Automation Mode" icon={Zap} description="Auto-scrape gaps & auto-classify per department" defaultExpanded={false} storageKey="automation">
            <AutomationPanel />
          </AdvancedSection>

          {/* DATA INTEGRITY */}
          <AdvancedSection title="Data Integrity" icon={ShieldCheck} description="Validation and quality checks" defaultExpanded={false} storageKey="data-integrity">
            <DataIntegrity departmentCode={deptCode} />
          </AdvancedSection>

          {/* PERFORMANCE METRICS */}
          <AdvancedSection title="Performance Metrics" icon={BarChart3} description="Scraper speed and efficiency" defaultExpanded={false} storageKey="performance-metrics">
            <ScraperMetricsPanel scrapeHistory={filteredHistory} onRefresh={fetchScrapeHistory} loading={historyLoading} hideHeader />
          </AdvancedSection>

          {/* LIVE LOGS */}
          <AdvancedSection
            title="Live Scraper Logs"
            icon={Terminal}
            description={`${logsConnected ? 'Connected' : 'Disconnected'} • ${scraperLogs.length} entries`}
            badge={logsConnected ? 'Live' : null}
            defaultExpanded={showLiveLogs}
            storageKey="live-logs"
          >
            <LogSummary
              stats={logsStats}
              connected={logsConnected}
              logs={scraperLogs}
            />
            <div style={{ marginTop: 'var(--space-3)' }}>
              <LiveLogs
                logs={scraperLogs}
                connected={logsConnected}
                error={logsError}
                usingPolling={usingPolling}
                onClear={clearLogs}
                getFilteredLogs={getFilteredLogs}
              />
            </div>
          </AdvancedSection>
        </React.Fragment>
      )}

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <SupplierDetailModal
          supplier={selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
        />
      )}

      {/* PO Comparison Modal */}
      {comparisonDocNum && (
        <div className="modal-overlay" onClick={() => setComparisonDocNum(null)}>
          <div className="modal-content modal-wide" onClick={e => e.stopPropagation()}>
            <POComparisonView
              documentNumber={comparisonDocNum}
              onClose={() => setComparisonDocNum(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ScraperDashboard;
